@if (projectId()) {
  <div class="settings">
    <div class="settings__header">
      <lib-headline
        [headline]="('settings.projectHeadline' | translate) + ' - ' + (projectName() ?? projectId())"
      ></lib-headline>
    </div>

    <lib-subheadline [subheadline]="'settings.integrationsSubheadline' | translate"></lib-subheadline>

    <app-project-integration-form
      [projectId]="projectId()!"
      [integrations]="projectIntegrations()"
      (saved)="onIntegrationSaved($event)"
    ></app-project-integration-form>
  </div>
} @else {
  <div class="settings">
    <div class="settings__page-header">
      <div class="settings__page-title-block">
        <h1 class="settings__title">{{ 'settings.headline' | translate }}</h1>
        <p class="settings__subtitle">
          {{ (reiter() === 'ki' ? 'settings.kiSubtitle' : 'settings.subtitle') | translate }}
        </p>
      </div>
      <div class="settings__header-actions">
        <button mat-stroked-button class="settings__cancel-btn" data-testid="settings-cancel-btn" (click)="onCancel()">
          {{ 'settings.cancel' | translate }}
        </button>
        <button
          mat-flat-button
          color="primary"
          class="settings__save-btn"
          data-testid="settings-save-btn"
          (click)="onSave()"
          [disabled]="(hasChanges() && form.invalid) || kiUngueltig() || !hatSpeicherbareAenderungen()"
        >
          <mat-icon aria-hidden="true">check</mat-icon>
          {{ 'settings.save' | translate }}
        </button>
      </div>
    </div>

    <div class="settings__reiter" role="tablist" [attr.aria-label]="'settings.reiter.label' | translate">
      <button
        type="button"
        role="tab"
        class="settings__reiter-tab"
        id="settings-reiter-allgemein"
        data-testid="settings-reiter-allgemein"
        aria-controls="settings-panel-allgemein"
        [class.settings__reiter-tab--aktiv]="reiter() === 'allgemein'"
        [attr.aria-selected]="reiter() === 'allgemein'"
        (click)="onReiterWaehlen('allgemein')"
      >
        {{ 'settings.reiter.allgemein' | translate }}
      </button>
      <button
        type="button"
        role="tab"
        class="settings__reiter-tab"
        id="settings-reiter-ki"
        data-testid="settings-reiter-ki"
        aria-controls="settings-panel-ki"
        [class.settings__reiter-tab--aktiv]="reiter() === 'ki'"
        [attr.aria-selected]="reiter() === 'ki'"
        (click)="onReiterWaehlen('ki')"
      >
        <app-ki-mark [size]="16" [inverted]="reiter() === 'ki'"></app-ki-mark>
        {{ 'settings.reiter.ki' | translate }}
      </button>
    </div>

    <div id="settings-panel-ki" role="tabpanel" aria-labelledby="settings-reiter-ki" [hidden]="reiter() !== 'ki'">
      @if (kiGeoeffnet()) {
        <app-ki-mandant-section [firmenname]="firmenname()"></app-ki-mandant-section>
      }
    </div>

    <div
      id="settings-panel-allgemein"
      role="tabpanel"
      aria-labelledby="settings-reiter-allgemein"
      [hidden]="reiter() !== 'allgemein'"
    >
      <div class="settings__card">
        <div class="settings__card-inner">
          <div class="settings__section-header">
            <div class="settings__section-title">{{ 'settings.brandingTitle' | translate }}</div>
            <div class="settings__section-subtitle">{{ 'settings.brandingSubtitle' | translate }}</div>
          </div>

          <div class="settings__grid-row">
            <div class="settings__grid-label">
              <div class="settings__field-title">{{ 'settings.colorTitle' | translate }}</div>
              <div class="settings__field-hint">{{ 'settings.colorHint' | translate }}</div>
            </div>
            <div class="settings__color-section">
              <div class="settings__color-row">
                <label
                  class="settings__color-swatch-label"
                  [attr.aria-label]="'settings.colorSwatchAriaLabel' | translate"
                  [title]="'settings.colorSwatchAriaLabel' | translate"
                >
                  <div class="settings__color-swatch" [style.background]="form.controls.color.value"></div>
                  <span class="settings__swatch-badge" aria-hidden="true">
                    <mat-icon class="settings__palette-icon">palette</mat-icon>
                  </span>
                  <input
                    type="color"
                    class="settings__color-picker-hidden"
                    [value]="form.controls.color.value"
                    (input)="onColorPickerChange($event)"
                    [attr.aria-label]="'settings.colorPickerAriaLabel' | translate"
                  />
                </label>
                <div class="settings__hex-wrapper">
                  <span class="settings__hex-label">HEX</span>
                  <input
                    class="settings__hex-input"
                    [formControl]="form.controls.color"
                    [attr.aria-label]="'settings.colorLabel' | translate"
                  />
                </div>
              </div>
              <div
                class="settings__color-presets"
                role="group"
                [attr.aria-label]="'settings.colorPresetsLabel' | translate"
              >
                @for (preset of colorPresets; track preset) {
                  <button
                    type="button"
                    class="settings__preset-btn"
                    [class.settings__preset-btn--active]="form.controls.color.value === preset"
                    [style.background]="preset"
                    [title]="preset"
                    [attr.aria-label]="('settings.colorPresetAriaLabel' | translate) + ' ' + preset"
                    [attr.aria-pressed]="form.controls.color.value === preset"
                    (click)="onPresetClick(preset)"
                  ></button>
                }
              </div>
              @if (form.controls.color.touched && form.controls.color.hasError('pattern')) {
                <p class="settings__field-error">{{ 'settings.colorInvalidError' | translate }}</p>
              }
              @if (form.controls.color.touched && form.controls.color.hasError('required')) {
                <p class="settings__field-error">{{ 'settings.colorRequiredError' | translate }}</p>
              }
            </div>
          </div>

          <mat-divider class="settings__inner-divider"></mat-divider>

          <div class="settings__grid-row">
            <div class="settings__grid-label">
              <div class="settings__field-title">{{ 'settings.logoTitle' | translate }}</div>
              <div class="settings__field-hint settings__logo-hint">{{ 'settings.logoHint' | translate }}</div>
            </div>
            <div>
              <div class="settings__logo-section">
                <div class="settings__logo-preview" [style.background-color]="previewBackground()">
                  <img
                    class="settings__logo-img"
                    [src]="previewLogoPath()"
                    [alt]="'settings.logoPreviewAlt' | translate"
                  />
                </div>
                <div class="settings__logo-actions">
                  <input
                    #logoFileInput
                    class="settings__logo-file-hidden"
                    type="file"
                    tabindex="-1"
                    aria-hidden="true"
                    [accept]="logoAccept"
                    (change)="onLogoFileSelected($event)"
                  />
                  <button mat-stroked-button type="button" (click)="logoFileInput.click()">
                    <mat-icon aria-hidden="true">image</mat-icon>
                    {{ 'settings.changeLogo' | translate }}
                  </button>
                  <button mat-stroked-button type="button" class="settings__btn-danger" (click)="onLogoDelete()">
                    <mat-icon aria-hidden="true">delete</mat-icon>
                    {{ 'settings.deleteLogo' | translate }}
                  </button>
                </div>
                <div class="settings__logo-tip">
                  <strong class="settings__logo-tip-label">{{ 'settings.logoTipLabel' | translate }}:</strong>
                  {{ 'settings.logoTip' | translate }}
                </div>
              </div>
              @if (logoError(); as fehler) {
                <p class="settings__logo-error" role="alert">{{ fehler | translate }}</p>
              }
            </div>
          </div>

          <mat-divider class="settings__inner-divider"></mat-divider>

          <div class="settings__grid-row">
            <div class="settings__grid-label">
              <div class="settings__field-title">{{ 'settings.nameTitle' | translate }}</div>
              <div class="settings__field-hint">{{ 'settings.nameHint' | translate }}</div>
            </div>
            <div class="settings__name-section">
              <div class="settings__name-wrapper">
                <input
                  class="settings__name-input"
                  [formControl]="form.controls.name"
                  maxlength="40"
                  [attr.aria-label]="'settings.nameLabel' | translate"
                />
                <span class="settings__name-counter" aria-live="polite">
                  {{ form.controls.name.value.length }} / 40
                </span>
              </div>
              @if (form.controls.name.touched && form.controls.name.hasError('maxlength')) {
                <p class="settings__field-error">{{ 'settings.nameMaxLengthError' | translate }}</p>
              }
            </div>
          </div>
        </div>
      </div>

      @if (canManageTenantUsers()) {
        <div class="settings__card settings__card--users">
          <div class="settings__card-inner">
            <div class="settings__users-header">
              <div>
                <div class="settings__section-title">{{ 'settings.userManagementTitle' | translate }}</div>
                <div class="settings__section-subtitle">{{ 'settings.userManagementSubtitle' | translate }}</div>
              </div>
            </div>

            <div class="settings__table-toolbar">
              <app-search-bar
                [placeholder]="'settings.userTable.search' | translate"
                (valueChange)="onSearchValueChange($event)"
              ></app-search-bar>
              <button
                type="button"
                class="settings__role-dropdown"
                [class.settings__role-dropdown--active]="activeRoleFilter() !== null"
                [matMenuTriggerFor]="roleMenu"
                [attr.aria-label]="'settings.userFilterRole' | translate"
              >
                {{
                  activeRoleFilter() !== null
                    ? ('roles.' + activeRoleFilter() | translate)
                    : ('settings.userFilterRole' | translate)
                }}
                <span class="settings__role-dropdown-caret" aria-hidden="true">▾</span>
              </button>
              <mat-menu #roleMenu="matMenu">
                <button mat-menu-item (click)="setRoleFilter(null)">
                  {{ 'settings.userFilterRoleAll' | translate }}
                </button>
                @for (option of roleFilterOptions; track option.code) {
                  <button mat-menu-item (click)="setRoleFilter(option.code)">
                    {{ option.labelKey | translate }}
                  </button>
                }
              </mat-menu>
            </div>

            @if (usersLoading()) {
              <div class="settings__users-loading">
                <mat-spinner diameter="40"></mat-spinner>
                <span>{{ 'settings.userTable.loading' | translate }}</span>
              </div>
            } @else if (usersError()) {
              <p class="settings__users-error">{{ usersError()! | translate }}</p>
            } @else {
              <div class="settings__users-table-wrapper" appScrollContainer>
                <table mat-table [dataSource]="dataSource">
                  <ng-container matColumnDef="name">
                    <th mat-header-cell *matHeaderCellDef scope="col">{{ 'settings.userTable.name' | translate }}</th>
                    <td mat-cell *matCellDef="let row">
                      <div class="settings__user-cell">
                        <app-avatar [name]="row.displayName" [size]="32"></app-avatar>
                        <span class="settings__user-name">{{ row.displayName }}</span>
                      </div>
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="email">
                    <th mat-header-cell *matHeaderCellDef scope="col">{{ 'settings.userTable.email' | translate }}</th>
                    <td mat-cell *matCellDef="let row">{{ row.email }}</td>
                  </ng-container>

                  <ng-container matColumnDef="role">
                    <th mat-header-cell *matHeaderCellDef scope="col">{{ 'settings.userTable.role' | translate }}</th>
                    <td mat-cell *matCellDef="let row">
                      <span class="settings__role-pill" [class]="'settings__role-pill--' + row.role.toLowerCase()">
                        <span class="settings__role-dot" aria-hidden="true"></span>
                        {{ getRoleDisplayName(row.role) }}
                      </span>
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="action">
                    <th mat-header-cell *matHeaderCellDef scope="col">{{ 'settings.userTable.action' | translate }}</th>
                    <td mat-cell *matCellDef="let row">
                      <button
                        mat-icon-button
                        [disabled]="isOwnUser(row.id)"
                        [matTooltip]="'settings.userTable.changeRole' | translate"
                        (click)="onChangeRole(row)"
                        [attr.aria-label]="'settings.userTable.changeRoleAriaLabel' | translate"
                      >
                        <mat-icon aria-hidden="true">manage_accounts</mat-icon>
                      </button>
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="userTableColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: userTableColumns" class="settings__user-row"></tr>

                  <tr class="mat-row" *matNoDataRow>
                    <td class="mat-cell" colspan="4">{{ 'settings.userTable.noData' | translate }}</td>
                  </tr>
                </table>
                <mat-paginator
                  [pageSizeOptions]="[10, 25, 50]"
                  [attr.aria-label]="'settings.userTable.paginatorAriaLabel' | translate"
                ></mat-paginator>
              </div>
            }
          </div>
        </div>
      }
    </div>

    @if (hatSpeicherbareAenderungen()) {
      <div class="settings__dirty-toast" role="status">
        <span class="settings__dirty-message">{{ 'settings.dirtyMessage' | translate }}</span>
        <button
          type="button"
          class="settings__toast-btn settings__toast-btn--discard"
          data-testid="settings-discard-btn"
          (click)="onDiscard()"
        >
          {{ 'settings.discard' | translate }}
        </button>
        <button
          type="button"
          class="settings__toast-btn settings__toast-btn--save"
          data-testid="settings-toast-save-btn"
          [disabled]="(hasChanges() && form.invalid) || kiUngueltig()"
          (click)="onSave()"
        >
          {{ 'settings.save' | translate }}
        </button>
      </div>
    }
  </div>
}

<lib-modal
  [visible]="showDeleteModal()"
  [title]="'settings.logoDeleteModalTitle' | translate"
  [text]="'settings.logoDeleteModalText' | translate"
  [saveText]="'settings.logoDeleteConfirm' | translate"
  [cancelText]="'settings.cancel' | translate"
  (save)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()"
  (visibleChange)="showDeleteModal.set($event)"
></lib-modal>

<lib-modal
  [title]="'settings.deactivateConfirm.title' | translate"
  [text]="'settings.deactivateConfirm.message' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'settings.deactivateConfirm.confirm' | translate"
  [cancelText]="'settings.deactivateConfirm.cancel' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>

<app-change-role-dialog
  [visible]="showRoleDialog()"
  [user]="selectedUser()"
  [roles]="roles()"
  (save)="onRoleSave($event)"
  (dialogCancel)="onRoleDialogCancel()"
  (visibleChange)="showRoleDialog.set($event)"
></app-change-role-dialog>
